import React, { useState, useEffect, useCallback } from 'react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { spacing } from './constants/designTokens';
import { phases, phaseKeys } from './constants/phases';
import { aspectRatios } from './constants/aspectRatios';
import { RENDER_CONFIG, estimateFileSize } from './constants/renderConfig';
import { patterns, konamiPattern } from './utils/patterns';
import { generateASCII as generateASCIIUtil } from './utils/asciiGenerator';

// Hooks
import { useAnimation } from './hooks/useAnimation';
import { useKonamiCode } from './hooks/useKonamiCode';
import { useMediaUpload } from './hooks/useMediaUpload';
import { useASCIISettings } from './hooks/useASCIISettings';
import { usePromptGeneration } from './hooks/usePromptGeneration';
import { useChatHistory } from './hooks/useChatHistory';
import { useDownloadHandler } from './hooks/useDownloadHandler';
import { useRandomizer } from './hooks/useRandomizer';
import { useGifExport } from './hooks/useGifExport';
import { useUIState } from './hooks/useUIState';
import { useToast } from './hooks/useToast';

// Components
import AppLayout from './components/layout/AppLayout.jsx';
import DesktopLayout from './components/layout/DesktopLayout.jsx';
import MobileLayout from './components/layout/MobileLayout.jsx';
import LeftSidebar from './components/layout/LeftSidebar.jsx';
import RightSidebar from './components/layout/RightSidebar.jsx';
import ModeToggle from './components/content/ModeToggle.jsx';
import CreativePromptView from './components/content/CreativePromptView.jsx';
import MobileCreativePromptView from './components/content/MobileCreativePromptView.jsx';
import PresetPhaseSelector from './components/content/PresetPhaseSelector.jsx';
import ASCIIPreview from './components/content/ASCIIPreview.jsx';
import Toast from './components/ui/Toast.jsx';
import FigmaModal from './components/modals/FigmaModal.jsx';
import AnimatedTransition from './components/ui/AnimatedTransition.jsx';
import Confetti from './components/ui/Confetti.jsx';
import GenerationSkeleton from './components/ui/GenerationSkeleton.jsx';

// Skeleton duration: 5 stages × 2200ms + 400ms pause at 100%
const SKELETON_MIN_DURATION = 5 * 2200 + 400;

const SignificaASCII = () => {
  const { colors } = useTheme();

  // Core state (not extracted to hooks)
  const [currentPhase, setCurrentPhase] = useState('think');
  const [mode, setMode] = useState('preset');
  const [isFrozen, setIsFrozen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [frozenFrame, setFrozenFrame] = useState(0);

  // Custom hooks
  const asciiSettings = useASCIISettings();
  const {
    settings,
    setDensity,
    setAnimationSpeed,
    setColumns,
    setGamma,
    setContrast,
    setCharacterCount,
    setAspectRatio,
    setCharacterSet,
    setGifBackgroundColor,
    setGifForegroundColor,
    setDimensionMode,
    setCustomWidth,
    setCustomHeight,
    updateSetting
  } = asciiSettings;

  const toast = useToast();
  const chatHistory = useChatHistory();

  const promptGen = usePromptGeneration({
    onSuccess: () => {
      setMode('creative');
      promptGen.setPrompt('');
    },
    onError: (message) => toast.showError(message),
    onPatternGenerated: (codeText, prompt) => {
      chatHistory.addAssistantMessage(prompt, codeText);
    }
  });

  const frame = useAnimation(settings.animationSpeed, isFrozen);
  const konamiMode = useKonamiCode();

  // Derived values
  let width, height;
  if (settings.dimensionMode === 'custom') {
    // Custom pixel mode: use a target fontSize to calculate character grid
    // Use a reasonable fontSize (e.g., 10px) to determine character density
    const targetFontSize = 10;
    const charWidth = targetFontSize * RENDER_CONFIG.fontWidthRatio;

    // Calculate how many characters fit in the custom pixel dimensions
    width = Math.round(settings.customWidth / charWidth);
    height = Math.round(settings.customHeight / targetFontSize);

    // Clamp to prevent extreme values
    width = Math.max(40, Math.min(1000, width));
    height = Math.max(20, Math.min(600, height));
  } else {
    // Aspect ratio mode (current behavior)
    width = settings.columns;
    const ratio = aspectRatios[settings.aspectRatio];
    height = Math.round((width * ratio.h) / (ratio.w * 2));
  }

  const mediaUpload = useMediaUpload(width, height);
  const {
    uploadedImage,
    uploadedVideo,
    videoFrames,
    currentVideoFrame,
    setCurrentVideoFrame,
    handleImageUpload: handleImageUploadHook,
    handleVideoUpload: handleVideoUploadHook,
    clearMedia
  } = mediaUpload;

  const hasUploadedMedia = !!uploadedImage || !!uploadedVideo || videoFrames.length > 0;

  const uiState = useUIState();
  const {
    copied,
    setCopied,
    showFigmaModal,
    setShowFigmaModal,
    summaryPulse,
    triggerPulse
  } = uiState;

  // Core business logic: generateASCII
  const generateASCII = useCallback((frameOverride) => {
    const t = frameOverride !== undefined ? frameOverride : frame;
    const pattern = konamiMode ? konamiPattern : (promptGen.customPattern || patterns[currentPhase]);

    return generateASCIIUtil(
      width,
      height,
      t,
      pattern,
      settings.characterSet,
      settings.characterCount,
      settings.density,
      settings.gamma,
      settings.contrast,
      videoFrames,
      currentVideoFrame
    );
  }, [frame, konamiMode, promptGen.customPattern, currentPhase, width, height, settings, videoFrames, currentVideoFrame]);

  const download = useDownloadHandler({
    generateASCII,
    width,
    height,
    columns: settings.columns,
    backgroundColor: settings.gifBackgroundColor,
    foregroundColor: settings.gifForegroundColor,
    dimensionMode: settings.dimensionMode,
    customWidth: settings.customWidth,
    customHeight: settings.customHeight
  });

  const randomizer = useRandomizer({
    settings,
    setDensity,
    setAnimationSpeed,
    setColumns,
    setGamma,
    setContrast,
    setCharacterCount,
    setGifBackgroundColor,
    setGifForegroundColor,
    mode,
    setCurrentPhase,
    characterSet: settings.characterSet,
    phaseKeys
  });

  const gifExport = useGifExport();

  // Effects
  useEffect(() => {
    if (videoFrames.length > 0) {
      const interval = setInterval(() => {
        setCurrentVideoFrame(prev => (prev + 1) % videoFrames.length);
      }, 1000 / RENDER_CONFIG.videoPlaybackFPS);
      return () => clearInterval(interval);
    }
  }, [videoFrames, setCurrentVideoFrame]);

  useEffect(() => {
    triggerPulse();
  }, [settings.columns, settings.aspectRatio, settings.characterCount, settings.dimensionMode, settings.customWidth, settings.customHeight, download.exportFormat, triggerPulse]);

  // Event handlers
  const handleCopy = useCallback(() => {
    const asciiText = generateASCII();
    navigator.clipboard.writeText(asciiText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), RENDER_CONFIG.toastDuration);
    }).catch(err => {
      console.error('Failed to copy:', err);
      toast.showError('Failed to copy text. Please try again.');
    });
  }, [generateASCII, setCopied, toast]);

  const handleModeChange = useCallback((newMode) => {
    setMode(newMode);
    if (newMode === 'preset') {
      promptGen.setCustomPattern(null);
      promptGen.setPrompt('');
      clearMedia();
    } else if (newMode === 'creative') {
      // Clear pattern when entering creative mode to start fresh
      promptGen.setCustomPattern(null);
      promptGen.setPrompt('');
      clearMedia();
    }
  }, [promptGen, clearMedia]);

  const handleDownload = useCallback(() => {
    if (download.exportFormat === 'gif') {
      gifExport.generateGif({
        width,
        height,
        columns: settings.columns,
        generateASCII,
        backgroundColor: settings.gifBackgroundColor,
        foregroundColor: settings.gifForegroundColor,
        dimensionMode: settings.dimensionMode,
        customWidth: settings.customWidth,
        customHeight: settings.customHeight
      });
    } else {
      download.handleDownload();
    }
  }, [download, gifExport, width, height, settings.columns, settings.gifBackgroundColor, settings.gifForegroundColor, settings.dimensionMode, settings.customWidth, settings.customHeight, generateASCII]);

  // Handle image/video uploads with mode and pattern setting
  const handleImageUpload = useCallback((e) => {
    handleImageUploadHook(e, promptGen.setCustomPattern, setMode);
  }, [handleImageUploadHook, promptGen.setCustomPattern]);

  const handleVideoUpload = useCallback((e) => {
    handleVideoUploadHook(e, promptGen.setCustomPattern, setMode);
  }, [handleVideoUploadHook, promptGen.setCustomPattern]);

  // Handle selecting a pattern from chat history
  const handleSelectHistoryPattern = useCallback((messageId) => {
    const patternFunc = chatHistory.getPatternFromMessage(messageId);
    if (patternFunc) {
      promptGen.setCustomPattern(() => patternFunc);
      chatHistory.selectMessage(messageId);
    }
  }, [chatHistory, promptGen]);

  // Handle generation - add user message first
  const handleGenerate = useCallback(() => {
    if (promptGen.prompt.trim()) {
      chatHistory.addUserMessage(promptGen.prompt);
    }
    promptGen.generateFromPrompt();
  }, [promptGen, chatHistory]);

  // Clear chat history and reset prompt/pattern/media
  const handleClearHistory = useCallback(() => {
    if (promptGen.isGenerating) {
      toast.showError('Cannot clear history while generating.');
      return;
    }
    chatHistory.clearHistory();
    promptGen.setCustomPattern(null);
    promptGen.setPrompt('');
    clearMedia();
    toast.showSuccess('Chat cleared');
  }, [chatHistory, promptGen, clearMedia, toast]);

  const estimatedSize = estimateFileSize(download.exportFormat, width, height, settings.characterCount);

  // Summary display changes based on dimension mode
  const summary = settings.dimensionMode === 'custom'
    ? `${settings.customWidth}×${settings.customHeight}px • ${settings.characterCount} chars • ${estimatedSize}`
    : `${settings.columns}px • ${aspectRatios[settings.aspectRatio].label} • ${settings.characterCount} chars • ${estimatedSize}`;

  // Track success for confetti
  const [showConfetti, setShowConfetti] = useState(false);

  // Track skeleton visual completion (all stages done)
  const [skeletonComplete, setSkeletonComplete] = useState(false);
  const [skeletonStartTime, setSkeletonStartTime] = useState(null);

  // Start timing when generation begins
  useEffect(() => {
    if (promptGen.isGenerating) {
      setSkeletonStartTime(Date.now());
      setSkeletonComplete(false);
    }
  }, [promptGen.isGenerating]);

  // Complete skeleton after minimum duration AND API is done
  useEffect(() => {
    if (skeletonStartTime && !promptGen.isGenerating && promptGen.customPattern && !skeletonComplete) {
      const elapsed = Date.now() - skeletonStartTime;
      const remaining = Math.max(0, SKELETON_MIN_DURATION - elapsed);

      const timer = setTimeout(() => {
        setSkeletonComplete(true);
      }, remaining);

      return () => clearTimeout(timer);
    }
  }, [skeletonStartTime, promptGen.isGenerating, promptGen.customPattern, skeletonComplete]);

  // Show skeleton only during actual API generation (not for image uploads)
  const showSkeleton = promptGen.isGenerating || (skeletonStartTime && !skeletonComplete);

  useEffect(() => {
    if (download.downloaded || gifExport.gifComplete) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [download.downloaded, gifExport.gifComplete]);

  // Render
  return (
    <AppLayout>
      <Confetti active={showConfetti} />
      <Toast toasts={toast.toasts} onDismiss={toast.dismissToast} />
      <FigmaModal isOpen={showFigmaModal} onClose={() => setShowFigmaModal(false)} />

      {/* Desktop Layout */}
      <DesktopLayout
        leftSidebar={
          <LeftSidebar
            characterSet={settings.characterSet}
            onCharacterSetChange={(newSet) => {
              setCharacterSet(newSet);
              if (settings.characterCount > newSet.length) {
                setCharacterCount(newSet.length);
              }
            }}
          />
        }
        mainContent={
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            minWidth: 0,
            background: colors.background
          }}>
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              padding: spacing.xxl,
              paddingBottom: spacing.xxxl,
              overflow: 'hidden'
            }}>
              <ModeToggle
                mode={mode}
                onModeChange={handleModeChange}
                hasPattern={!!promptGen.customPattern && !showSkeleton}
                onReset={() => {
                  promptGen.setCustomPattern(null);
                  promptGen.setPrompt('');
                  clearMedia();
                }}
              />

              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: (mode === 'creative' && (showSkeleton || !chatHistory.hasHistory)) ? 'center' : 'flex-start',
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
                <AnimatedTransition transitionKey={showSkeleton ? 'skeleton' : `${mode}-chat`}>
                  {mode === 'creative' && showSkeleton ? (
                    <GenerationSkeleton userPrompt={promptGen.prompt} />
                  ) : mode === 'creative' ? (
                    <CreativePromptView
                      prompt={promptGen.prompt}
                      onPromptChange={promptGen.setPrompt}
                      isGenerating={promptGen.isGenerating}
                      isDisabled={promptGen.isDisabled}
                      disabledUntil={promptGen.disabledUntil}
                      onGenerate={handleGenerate}
                      onImageUpload={handleImageUpload}
                      onVideoUpload={handleVideoUpload}
                      messages={chatHistory.messages}
                      selectedMessageId={chatHistory.selectedMessageId}
                      onSelectMessage={handleSelectHistoryPattern}
                      // When an uploaded media exists, lock text generation
                      hasUploadedMedia={hasUploadedMedia}
                      uploadedImageUrl={uploadedImage}
                      uploadedVideoUrl={uploadedVideo}
                      isMediaReady={videoFrames.length > 0}
                      // ASCII Preview props
                      asciiText={promptGen.customPattern ? (isFrozen ? generateASCII(frozenFrame) : generateASCII()) : null}
                      columns={settings.columns}
                      isFullscreen={isFullscreen}
                      onFullscreenClose={() => setIsFullscreen(false)}
                      onFreeze={() => {
                        setIsFrozen(true);
                        setFrozenFrame(frame);
                      }}
                      onUnfreeze={() => setIsFrozen(false)}
                      isFrozen={isFrozen}
                      backgroundColor={settings.gifBackgroundColor}
                      foregroundColor={settings.gifForegroundColor}
                      // Clear history handler
                      onClearHistory={handleClearHistory}
                    />
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: spacing.xxl,
                      width: '100%',
                      padding: `${spacing.xl} 0`
                    }}>
                      {mode === 'preset' && (
                        <PresetPhaseSelector
                          currentPhase={currentPhase}
                          onPhaseChange={setCurrentPhase}
                          phases={phases}
                          phaseKeys={phaseKeys}
                        />
                      )}

                      <ASCIIPreview
                        asciiText={isFrozen ? generateASCII(frozenFrame) : generateASCII()}
                        columns={settings.columns}
                        onFreeze={() => {
                          setIsFrozen(true);
                          setFrozenFrame(frame);
                        }}
                        onUnfreeze={() => setIsFrozen(false)}
                        isFrozen={isFrozen}
                        backgroundColor={settings.gifBackgroundColor}
                        foregroundColor={settings.gifForegroundColor}
                        isFullscreen={isFullscreen}
                        onFullscreenClose={() => setIsFullscreen(false)}
                      />
                    </div>
                  )}
                </AnimatedTransition>
              </div>
            </div>
          </div>
        }
        rightSidebar={
          <RightSidebar
            settings={settings}
            mode={mode}
            hasPattern={(!!promptGen.customPattern && !showSkeleton) || mode === 'preset'}
            onRandomize={randomizer.randomizeSettings}
            isRandomizing={randomizer.isRandomizing}
            hasUploadedMedia={videoFrames.length > 0}
            onDensityChange={(e) => setDensity(parseFloat(e.target.value))}
            onColumnsChange={(e) => setColumns(parseInt(e.target.value))}
            onAspectRatioChange={setAspectRatio}
            onGammaChange={(e) => setGamma(parseFloat(e.target.value))}
            onContrastChange={(e) => setContrast(parseFloat(e.target.value))}
            onCharacterCountChange={(e) => setCharacterCount(parseInt(e.target.value))}
            onAsciiStyleChange={(style) => updateSetting('asciiStyle', style)}
            onCharacterSetChange={(e) => {
              const newSet = e.target.value;
              setCharacterSet(newSet);
              if (settings.characterCount > newSet.length) {
                setCharacterCount(newSet.length);
              }
            }}
            onGifBackgroundColorChange={setGifBackgroundColor}
            onGifForegroundColorChange={setGifForegroundColor}
            onDimensionModeChange={setDimensionMode}
            onCustomWidthChange={setCustomWidth}
            onCustomHeightChange={setCustomHeight}
            // Export controls
            exportFormat={download.exportFormat}
            onExportFormatChange={download.setExportFormat}
            onFullscreen={() => setIsFullscreen(true)}
            summary={summary}
            summaryPulse={summaryPulse}
            onDownload={handleDownload}
            onCopy={handleCopy}
            isDownloading={download.isDownloading}
            downloaded={download.downloaded}
            copied={copied}
            gifProgress={gifExport.gifProgress.percent}
            isGeneratingGif={gifExport.isGeneratingGif}
            gifComplete={gifExport.gifComplete}
          />
        }
      />

      {/* Mobile Layout */}
      <MobileLayout
        // Action Bar props
        onDownload={handleDownload}
        onCopy={handleCopy}
        isDownloading={download.isDownloading}
        downloaded={download.downloaded}
        copied={copied}
        summary={summary}

        // Content props
        mode={mode}
        modeToggle={
          <ModeToggle
            mode={mode}
            onModeChange={handleModeChange}
            hasPattern={!!promptGen.customPattern && !showSkeleton}
            onReset={() => {
              promptGen.setCustomPattern(null);
              promptGen.setPrompt('');
              clearMedia();
            }}
          />
        }
        currentPhase={currentPhase}
        onPhaseChange={setCurrentPhase}
        phases={phases}
        phaseKeys={phaseKeys}
        asciiPreview={
          <ASCIIPreview
            asciiText={isFrozen ? generateASCII(frozenFrame) : generateASCII()}
            columns={settings.columns}
            onFreeze={() => {
              setIsFrozen(true);
              setFrozenFrame(frame);
            }}
            onUnfreeze={() => setIsFrozen(false)}
            isFrozen={isFrozen}
            backgroundColor={settings.gifBackgroundColor}
            foregroundColor={settings.gifForegroundColor}
            isFullscreen={isFullscreen}
            onFullscreenClose={() => setIsFullscreen(false)}
          />
        }
        creativePromptView={
          mode === 'creative' && showSkeleton ? (
            <GenerationSkeleton userPrompt={promptGen.prompt} />
          ) : mode === 'creative' ? (
            <MobileCreativePromptView
              prompt={promptGen.prompt}
              onPromptChange={promptGen.setPrompt}
              isGenerating={promptGen.isGenerating}
              isDisabled={promptGen.isDisabled}
              disabledUntil={promptGen.disabledUntil}
              onGenerate={handleGenerate}
              onImageUpload={handleImageUpload}
              onVideoUpload={handleVideoUpload}
              messages={chatHistory.messages}
              selectedMessageId={chatHistory.selectedMessageId}
              onSelectMessage={handleSelectHistoryPattern}
              // When an uploaded media exists, lock text generation
              hasUploadedMedia={hasUploadedMedia}
              uploadedImageUrl={uploadedImage}
              uploadedVideoUrl={uploadedVideo}
              isMediaReady={videoFrames.length > 0}
              // ASCII Preview props
              asciiText={promptGen.customPattern ? (isFrozen ? generateASCII(frozenFrame) : generateASCII()) : null}
              columns={settings.columns}
              isFullscreen={isFullscreen}
              onFullscreenClose={() => setIsFullscreen(false)}
              onFreeze={() => {
                setIsFrozen(true);
                setFrozenFrame(frame);
              }}
              onUnfreeze={() => setIsFrozen(false)}
              isFrozen={isFrozen}
              backgroundColor={settings.gifBackgroundColor}
              foregroundColor={settings.gifForegroundColor}
              // Clear history handler
              onClearHistory={handleClearHistory}
            />
          ) : null
        }

        // Control Panel props
        settings={settings}
        hasUploadedMedia={videoFrames.length > 0}
        onDensityChange={(e) => setDensity(parseFloat(e.target.value))}
        onColumnsChange={(e) => setColumns(parseInt(e.target.value))}
        onAspectRatioChange={setAspectRatio}
        onGammaChange={(e) => setGamma(parseFloat(e.target.value))}
        onContrastChange={(e) => setContrast(parseFloat(e.target.value))}
        onCharacterCountChange={(e) => setCharacterCount(parseInt(e.target.value))}
        onAsciiStyleChange={(style) => updateSetting('asciiStyle', style)}
        onGifBackgroundColorChange={setGifBackgroundColor}
        onGifForegroundColorChange={setGifForegroundColor}
        onDimensionModeChange={setDimensionMode}
        onCustomWidthChange={setCustomWidth}
        onCustomHeightChange={setCustomHeight}
        gifProgress={gifExport.gifProgress.percent}
        isGeneratingGif={gifExport.isGeneratingGif}
        gifComplete={gifExport.gifComplete}
      />
    </AppLayout>
  );
};

// Wrap with ThemeProvider
const App = () => (
  <ThemeProvider>
    <SignificaASCII />
  </ThemeProvider>
);

export default App;
